import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Paper,
  Chip,
  Stack,
} from "@mui/material";
import { CalendarToday, CheckCircle, Link, LinkOff } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import ActionsService from "../../services/actions.service";
import GoogleCalendarDialog from "./GoogleCalendarDialog";
import LoaderOverlay from "../../components/LoaderOverlay";
import FlowProgress from "../../components/FlowProgress";

const ActionsIntegration = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // const user = JSON.parse(localStorage.getItem("user"));

  // Google state
  const [activeConnection, setActiveConnection] = useState(null);
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [loading, setLoading] = useState(true);

  // Fetch connection + events - ✅ Google integration
  useEffect(() => {
    const fetchConnection = async () => {
      try {
        const connected = searchParams.get("connected");
        const conn = await ActionsService.getConnection();

        if ((connected === "true" || conn) && conn) {
          setActiveConnection(conn);
          localStorage.setItem("activeConnection", JSON.stringify(conn));

          const evts = await ActionsService.listEvents({});

          // ✅ Normalize here
          const normalized = (evts || []).map((evt) => ({
            id: evt.id || Date.now().toString(),
            title: evt.title || evt.summary || "Untitled",
            start: evt.start?.dateTime || evt.start?.date || evt.start,
            end: evt.end?.dateTime || evt.end?.date || evt.end,
          }));

          setEvents(normalized);

          if (connected === "true") {
            navigate("/app/actions", { replace: true });
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchConnection();
  }, [searchParams, navigate]);

  // Google handlers
  const handleConnectClick = async () => {
    try {
      const { url } = await ActionsService.getAuthUrl();
      window.location.href = url;
    } catch (err) {
      console.error("❌ OAuth start error:", err);
      alert("Failed to start Google OAuth flow");
    }
  };

  const handleDisconnect = async () => {
    try {
      await ActionsService.disconnect();
      localStorage.removeItem("activeConnection");
      setActiveConnection(null);
      setEvents([]);
    } catch (err) {
      console.error("❌ Disconnect error:", err);
    }
  };

  return (
    <>
      <Helmet>
        <title>Connect Your Tools to Your Agent | SitePilot</title>
        <meta
          name="description"
          content="Connect your business tools so your agent can take actions for customers through integrations."
        />
      </Helmet>

      <LoaderOverlay open={loading} />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Flow Progress Tracker */}
        <FlowProgress currentPage="actions" variant="compact" />

        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Connect Your Tools to Your Agent ⚙️
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto", fontSize: "1.1rem" }}
          >
            Connect Google Calendar so your agent can schedule meetings for
            customers
          </Typography>
        </Box>

        {/* Integration Cards */}
        <Grid container spacing={4} justifyContent="center">
          {/* Google Calendar Integration */}
          <Grid item xs={12} sm={8} md={6}>
            <Paper
              sx={{
                p: 4,
                borderRadius: 4,
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                transition: "all 0.3s ease",
                position: "relative",
                overflow: "hidden",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
                  border: "1px solid #667eea",
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: activeConnection
                    ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                },
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                {/* Icon */}
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 3,
                    background: activeConnection
                      ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  }}
                >
                  <CalendarToday sx={{ fontSize: 28, color: "white" }} />
                </Box>

                {/* Title */}
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
                >
                  Google Calendar
                </Typography>

                {/* Description */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3, lineHeight: 1.6 }}
                >
                  Let your agent schedule meetings and manage appointments
                </Typography>

                {/* Status */}
                {activeConnection ? (
                  <Stack spacing={2} alignItems="center">
                    <Chip
                      icon={<CheckCircle />}
                      label="Connected"
                      color="success"
                      variant="filled"
                      sx={{
                        fontWeight: 600,
                        px: 2,
                        py: 1,
                        "& .MuiChip-icon": {
                          fontSize: 18,
                        },
                      }}
                    />
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<LinkOff />}
                      onClick={handleDisconnect}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        fontWeight: 600,
                        borderColor: "#ef4444",
                        color: "#ef4444",
                        "&:hover": {
                          borderColor: "#dc2626",
                          backgroundColor: "rgba(239, 68, 68, 0.04)",
                        },
                      }}
                    >
                      Disconnect
                    </Button>
                  </Stack>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<Link />}
                    onClick={handleConnectClick}
                    sx={{
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      fontWeight: 700,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                        boxShadow: "0 6px 16px rgba(102, 126, 234, 0.5)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    Connect Google Calendar
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Help Text */}
        <Box
          sx={{
            mt: 6,
            p: 4,
            backgroundColor: "rgba(102, 126, 234, 0.04)",
            borderRadius: 3,
            border: "1px solid rgba(102, 126, 234, 0.1)",
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>💡 Note:</strong> Email capture is always enabled.
            Connecting Google Calendar allows your agent to schedule meetings
            for customers who want to talk.
          </Typography>
        </Box>
      </Container>

      {/* Events Dialog */}
      <GoogleCalendarDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        events={events}
        setEvents={setEvents}
        onDisconnect={handleDisconnect}
      />
    </>
  );
};

export default ActionsIntegration;
